import { vec3 } from 'gl-matrix';
import { Scene } from '../Scene';
import { SceneFeature } from './SceneFeature';
import { DirectionalLight } from '../../light';
import { Sky } from '../../object/primitives/Sky';
import { SkyBox, RotationSource } from '../../object/primitives/SkyBox';
import { ProceduralSky } from '../../object/primitives/ProceduralSky';
import { ImageCubeMapTexture } from '../../texture';

type Color3 = [number, number, number];

interface DayNightKeyframe {
  time: number;
  color: Color3;
  ambient: Color3;
  skyTint: Color3;
  intensity: number;
}

const KEYFRAMES: DayNightKeyframe[] = [
  {
    time: 0,
    color: [0.4, 0.5, 0.7],
    ambient: [0.05, 0.05, 0.08],
    skyTint: [0.05, 0.05, 0.15],
    intensity: 0.3,
  }, // midnight
  {
    time: 45,
    color: [0.6, 0.45, 0.5],
    ambient: [0.06, 0.06, 0.08],
    skyTint: [0.3, 0.15, 0.2],
    intensity: 0.35,
  }, // pre-dawn
  {
    time: 75,
    color: [1.0, 0.6, 0.3],
    ambient: [0.12, 0.1, 0.08],
    skyTint: [0.8, 0.5, 0.3],
    intensity: 0.6,
  }, // dawn
  {
    time: 120,
    color: [1.0, 0.95, 0.85],
    ambient: [0.22, 0.22, 0.22],
    skyTint: [1.0, 1.0, 0.95],
    intensity: 0.95,
  }, // morning
  {
    time: 180,
    color: [1.0, 0.95, 0.8],
    ambient: [0.25, 0.25, 0.25],
    skyTint: [1.0, 1.0, 1.0],
    intensity: 1.0,
  }, // noon
  {
    time: 240,
    color: [1.0, 0.95, 0.85],
    ambient: [0.22, 0.22, 0.22],
    skyTint: [1.0, 1.0, 0.95],
    intensity: 0.95,
  }, // afternoon
  {
    time: 285,
    color: [1.0, 0.5, 0.25],
    ambient: [0.12, 0.1, 0.08],
    skyTint: [0.7, 0.4, 0.2],
    intensity: 0.6,
  }, // dusk
  {
    time: 315,
    color: [0.6, 0.45, 0.5],
    ambient: [0.06, 0.06, 0.08],
    skyTint: [0.3, 0.15, 0.2],
    intensity: 0.35,
  }, // post-dusk
];

function catmullRom(p0: number, p1: number, p2: number, p3: number, t: number): number {
  return (
    0.5 *
    (2 * p1 +
      (-p0 + p2) * t +
      (2 * p0 - 5 * p1 + 4 * p2 - p3) * t * t +
      (-p0 + 3 * p1 - 3 * p2 + p3) * t * t * t)
  );
}

function catmullRomColor(p0: Color3, p1: Color3, p2: Color3, p3: Color3, t: number): Color3 {
  return [
    Math.max(0, catmullRom(p0[0], p1[0], p2[0], p3[0], t)),
    Math.max(0, catmullRom(p0[1], p1[1], p2[1], p3[1], t)),
    Math.max(0, catmullRom(p0[2], p1[2], p2[2], p3[2], t)),
  ];
}

export function computeDayNightState(timeOfDay: number) {
  const t = ((timeOfDay % 360) + 360) % 360;
  const n = KEYFRAMES.length;

  let i1 = n - 1;
  for (let i = 0; i < n; i++) {
    if (KEYFRAMES[i].time > t) {
      i1 = i - 1;
      break;
    }
  }

  const idx = (i: number) => KEYFRAMES[((i % n) + n) % n];
  const k0 = idx(i1 - 1),
    k1 = idx(i1),
    k2 = idx(i1 + 1),
    k3 = idx(i1 + 2);

  let segStart = k1.time;
  let segEnd = k2.time;
  if (segEnd <= segStart) segEnd += 360;
  let localT = t >= segStart ? t : t + 360;
  const p = (localT - segStart) / (segEnd - segStart);

  return {
    color: catmullRomColor(k0.color, k1.color, k2.color, k3.color, p),
    ambient: catmullRomColor(k0.ambient, k1.ambient, k2.ambient, k3.ambient, p),
    skyTint: catmullRomColor(k0.skyTint, k1.skyTint, k2.skyTint, k3.skyTint, p),
    intensity: Math.max(0, catmullRom(k0.intensity, k1.intensity, k2.intensity, k3.intensity, p)),
  };
}

export interface DayNightCycleOptions {
  /**
   * What kind of sky to render. `'image'` (the default) loads the legacy
   * cubemap from {@link skybox}; `'procedural'` renders an analytical
   * Rayleigh+Mie atmosphere driven by the sun the feature creates.
   */
  skyType?: 'image' | 'procedural';
  /** Source directory for the image cubemap. Ignored when skyType is procedural. */
  skybox?: string;
  startTime?: number;
  speed?: number;
  sunReference?: string;
  enabled?: boolean;
  /** Optional ProceduralSky appearance overrides (applied when skyType is procedural). */
  sunColor?: number[];
  sunIntensity?: number;
  fogColor?: number[];
  horizonMist?: number;
  exposure?: number;
  showSun?: boolean;
  showStars?: boolean;
  showMoon?: boolean;
  showClouds?: boolean;
  cloudColor?: number[];
  cloudShadowColor?: number[];
  cloudCoverage?: number;
  cloudDensity?: number;
  cloudScale?: number;
  cloudWind?: number[];
  cloudWindX?: number;
  cloudWindY?: number;
}

export class DayNightCycleFeature implements SceneFeature {
  public timeOfDay: number;
  public speed: number;
  public enabled: boolean = true;

  private sunReference: string;
  private skyboxSource?: string;
  private skyType: 'image' | 'procedural';
  private skyOptions: DayNightCycleOptions;
  private scene!: Scene;
  private sun!: DirectionalLight;
  private sky?: Sky;

  constructor(options: DayNightCycleOptions) {
    this.timeOfDay = options.startTime ?? 90;
    this.speed = options.speed ?? 10;
    this.enabled = options.enabled ?? true;
    this.sunReference = options.sunReference ?? 'sun';
    this.skyboxSource = options.skybox;
    this.skyType = options.skyType ?? 'image';
    this.skyOptions = options;
  }

  async init(scene: Scene): Promise<void> {
    this.scene = scene;

    // Adopt an existing directional light with the sun reference if one was already loaded
    // (e.g. from manifest.lights[]); otherwise create a new one.
    const existing = scene.lightManager.lights.find(
      (l): l is DirectionalLight =>
        l instanceof DirectionalLight && l.reference === this.sunReference,
    ) ?? null;
    if (existing) {
      this.sun = existing;
    } else {
      this.sun = new DirectionalLight({
        reference: this.sunReference,
        position: [0, 100, 0],
        direction: [0.5, -1, 0.3],
        color: vec3.fromValues(1.0, 0.95, 0.8),
        shadowMode: 'dynamic',
        priority: 20,
      });
      scene.lightManager.addLight(this.sun);
    }

    // Create the sky
    if (this.skyType === 'procedural') {
      const sky = new ProceduralSky(scene.ctx, scene);
      this.applySkyOptions(sky);
      this.sky = sky;
      scene.objectManager.addObject(this.sky);
    } else if (this.skyboxSource) {
      const cube = new ImageCubeMapTexture({
        sourceDirectory: this.skyboxSource,
      });
      await cube.load(scene.ctx);
      const skybox = new SkyBox(
        scene.ctx,
        scene.ctx.engine.activeCamera as unknown as RotationSource,
      );
      skybox.cubeTexture = cube;
      this.sky = skybox;
      scene.objectManager.addObject(this.sky);
    }

    // Apply initial state
    this.apply();
  }

  tick(deltaTime: number): void {
    if (!this.enabled) return;
    this.timeOfDay = (this.timeOfDay + this.speed * deltaTime) % 360;
    this.apply();
  }

  private applySkyOptions(sky: ProceduralSky): void {
    const o = this.skyOptions;
    if (o.sunColor) sky.sunColor = o.sunColor as [number, number, number];
    if (o.sunIntensity !== undefined) sky.sunIntensity = o.sunIntensity;
    if (o.fogColor) sky.fogColor = o.fogColor as [number, number, number];
    if (o.horizonMist !== undefined) sky.horizonMist = o.horizonMist;
    if (o.exposure !== undefined) sky.exposure = o.exposure;
    if (o.showSun !== undefined) sky.showSun = o.showSun;
    if (o.showStars !== undefined) sky.showStars = o.showStars;
    if (o.showMoon !== undefined) sky.showMoon = o.showMoon;
    if (o.showClouds !== undefined) sky.showClouds = o.showClouds;
    if (o.cloudColor) sky.cloudColor = o.cloudColor as [number, number, number];
    if (o.cloudShadowColor) sky.cloudShadowColor = o.cloudShadowColor as [number, number, number];
    if (o.cloudCoverage !== undefined) sky.cloudCoverage = o.cloudCoverage;
    if (o.cloudDensity !== undefined) sky.cloudDensity = o.cloudDensity;
    if (o.cloudScale !== undefined) sky.cloudScale = o.cloudScale;
    if (o.cloudWind) sky.cloudWind = o.cloudWind as [number, number];
    else if (o.cloudWindX !== undefined || o.cloudWindY !== undefined)
      sky.cloudWind = [o.cloudWindX ?? sky.cloudWind[0], o.cloudWindY ?? sky.cloudWind[1]];
  }

  private apply(): void {
    const state = computeDayNightState(this.timeOfDay);

    // Sun arcs east → overhead → west → underfoot in the XY plane (z=0).
    // Convention from KEYFRAMES: 0°=midnight, 90°=sunrise, 180°=noon, 270°=sunset.
    // Sun position parametrised as (sin t, -cos t, 0) gives:
    //   midnight: (0,-1,0) — directly below
    //   sunrise:  (1, 0,0) — east horizon
    //   noon:     (0, 1,0) — directly above
    //   sunset:  (-1, 0,0) — west horizon
    // Light direction is the opposite (the way light travels).
    const timeRad = (this.timeOfDay * Math.PI) / 180;
    const x = -Math.sin(timeRad);
    const y = Math.cos(timeRad);
    const z = 0;

    this.sun.direction = [x, y, z];
    this.sun.color = [
      state.color[0] * state.intensity,
      state.color[1] * state.intensity,
      state.color[2] * state.intensity,
    ];
    this.sun.markDirty();

    this.scene.lightManager.ambientColor = vec3.fromValues(
      state.ambient[0],
      state.ambient[1],
      state.ambient[2],
    );

    if (this.sky) {
      this.sky.tintColor = state.skyTint;
    }
  }
}
